import { useOidc } from "@axa-fr/react-oidc";
import { environment } from "@org-avp/avp-avengers-ui-shared-environment";
import { setUserData } from "@org-avp/avp-avengers-ui-shared-store";
import { FC, useCallback, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { OidcAppConfiguration, OidcContext } from "../../types/oidcTypes";
import { createRedirectUri, logoutFromNonDefaultOidcContexts } from "../../utils/oidcUtils";
import { OidcLoggingOut } from "../internal/OidcStateComponents";

const logoutStateKey = "logout_state";

enum LogoutState {
  INITIAL = "initial",
  ASYNC_LOGOUTS_DONE = "async_logouts_done",
  COGNITO_CALLBACK = "cognito_callback",
  FINISHED = "finished",
}

export interface LogoutViewProps {
  oidcAppConfiguration: OidcAppConfiguration;
}

export const DefaultLogoutView: FC<LogoutViewProps> = ({ oidcAppConfiguration }) => {
  if (oidcAppConfiguration.oidcContext !== OidcContext.DEFAULT) {
    throw new Error(`Expected default OIDC context, got: ${oidcAppConfiguration.oidcContext}`);
  }

  const [searchParams, setSearchParams] = useSearchParams();
  const updateState = useCallback(
    (logoutState: LogoutState) => {
      setSearchParams({ [logoutStateKey]: logoutState });
    },
    [setSearchParams],
  );

  const { logout } = useOidc(oidcAppConfiguration.oidcContext);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(
    () => {
      const executeAsyncOidcLogouts = () => {
        logoutFromNonDefaultOidcContexts() // logout asynchronously from other OIDC contexts
          .finally(() => updateState(LogoutState.ASYNC_LOGOUTS_DONE));
      };

      const logoutWithCognito = () => {
        const redirectPath = `${oidcAppConfiguration.pathLogout}?${logoutStateKey}=${LogoutState.COGNITO_CALLBACK}`;
        location.href = createLogoutUrl(environment.NX_LOGOUT_URL_COGNITO, redirectPath);
      };

      const finalizeLogout = () => {
        // "no_reload:oidc" prevents the OIDC client from redirecting to the IdP
        logout(null, { "no_reload:oidc": "true" })
          .finally(() => {
            dispatch(setUserData(null));
            updateState(LogoutState.FINISHED);
          });
      };

      switch (getLogoutState(searchParams)) {
        case LogoutState.INITIAL:
          executeAsyncOidcLogouts();
          break;
        case LogoutState.ASYNC_LOGOUTS_DONE:
          logoutWithCognito();
          break;
        case LogoutState.COGNITO_CALLBACK:
          finalizeLogout();
          break;
        case LogoutState.FINISHED:
          navigate(oidcAppConfiguration.pathLogin);
          break;
      }
    },
    [
      dispatch,
      logout,
      navigate,
      oidcAppConfiguration.oidcContext,
      oidcAppConfiguration.pathLogin,
      oidcAppConfiguration.pathLogout,
      searchParams,
      updateState,
    ],
  );

  return (<OidcLoggingOut/>);
};

function createLogoutUrl(logoutUrl: string, redirectPath: string): string {
  return logoutUrl + encodeURIComponent(createRedirectUri(redirectPath));
}

function getLogoutState(searchParams: URLSearchParams): LogoutState {
  const logoutState = searchParams.get(logoutStateKey);
  return (logoutState ?? LogoutState.INITIAL) as LogoutState;
}
